// ragService.js - Service for RAG document operations
import { getCanonicalFolderName } from '../utils/filenameUtils';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import app from '../../../firebase';
import { getAuth } from 'firebase/auth';
import { getFirebaseAuthToken } from '../../../utils/firebaseAuthToken';

const ITINERARY_STORAGE_PROXY_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendSaveItineraryJsonToFirebase';

export class RAGService {
  constructor() {
    this.baseStorageUrl = 'https://storage.googleapis.com/ai-tourguide-1dc92.firebasestorage.app';
  }

  /**
   * Convert Firebase Storage REST API URL to direct Cloud Storage URL
   * FROM: https://firebasestorage.googleapis.com/v0/b/BUCKET/o/PATH?alt=media
   * TO: https://storage.googleapis.com/BUCKET/PATH
   */
  convertToDirectStorageUrl(url) {
    if (!url) return url;

    // If already in direct format, return as is
    if (url.startsWith('https://storage.googleapis.com/')) {
      return url;
    }

    // Convert from Firebase Storage REST API format
    if (url.includes('firebasestorage.googleapis.com/v0/b/')) {
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/o/');
        if (pathParts.length === 2) {
          const bucket = pathParts[0].replace('/v0/b/', '');
          const path = decodeURIComponent(pathParts[1]);
          return `https://storage.googleapis.com/${bucket}/${path}`;
        }
      } catch (error) {
        console.error('Error converting URL:', error);
        return url;
      }
    }

    return url;
  }

  getFolderPath(metadata) {
    if (!metadata?.country || (!metadata?.filename && !metadata?.name)) {
      throw new Error('Country and filename or display name are required for storage paths');
    }

    const country = metadata.country.toLowerCase().replace(/[^\w]/g, '_').replace(/_+/g, '_');
    const folderPath = getCanonicalFolderName(metadata);

    return {
      country,
      folderPath
    };
  }

  /**
   * Generate RAG manifest URL for an itinerary
   */
  generateManifestUrl(metadata) {
    const { country, folderPath } = this.getFolderPath(metadata);
    return `${this.baseStorageUrl}/special_itineraries/${country}/${folderPath}/rag_documents/manifest.json`;
  }

  /**
   * Generate base URL for RAG documents
   */
  generateDocumentsBaseUrl(metadata) {
    const { country, folderPath } = this.getFolderPath(metadata);
    return `${this.baseStorageUrl}/special_itineraries/${country}/${folderPath}/rag_documents/`;
  }

  /**
   * Format language code consistently
   */
  formatLanguageCode(code) {
    if (!code) return 'en-us';
    const parts = code.split(/[-_]/);
    if (parts.length === 1) {
      const lang = parts[0].toLowerCase();
      return (lang === 'en') ? 'en-us' : `${lang}-${lang}`;
    } else {
      return `${parts[0].toLowerCase()}-${parts[1].toLowerCase()}`;
    }
  }

  /**
   * Sanitize itinerary name for file paths
   */
  sanitizeItineraryName(name) {
    if (!name) return '';
    return name
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[\s'-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  /**
   * Load RAG manifest using fetchGuide function (same as main JSON files)
   */
  async loadManifest(metadata) {
    try {
      const { country, folderPath } = this.getFolderPath(metadata);

      // Construct the storage URL for fetchGuide function
      const manifestUrl = `https://storage.googleapis.com/ai-tourguide-1dc92.firebasestorage.app/special_itineraries/${country}/${folderPath}/rag_documents/manifest.json`;

      console.log('Loading RAG manifest via fetchGuide:', manifestUrl);

      // Use the fetchGuide function (same as main JSON files)
      const fetchUrl = `https://us-central1-guidesofia-access-control.cloudfunctions.net/fetchGuide?url=${encodeURIComponent(manifestUrl)}`;

      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in');
      }

      const token = await currentUser.getIdToken(true);
      const response = await fetch(fetchUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          // File doesn't exist
          throw new Error('MANIFEST_NOT_FOUND');
        }
        throw new Error(`Failed to load manifest: ${response.status} ${response.statusText}`);
      }

      const manifest = await response.json();
      return manifest;
    } catch (error) {
      if (error.message === 'MANIFEST_NOT_FOUND') {
        throw error; // Re-throw specific error for handling in component
      }
      throw new Error(`Error loading RAG manifest: ${error.message}`);
    }
  }

  /**
   * Save RAG manifest to cloud storage via backend function
   */
  async saveManifest(manifest, metadata) {
    try {
      // Regenerate global keywords before saving
      const updatedManifest = this.updateManifestGlobalKeywords(manifest);
      const { country, folderPath } = this.getFolderPath(metadata);
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to save the RAG manifest');
      }

      const token = await getFirebaseAuthToken(currentUser);

      // Use the same backend function as regular itinerary files
      const response = await fetch(ITINERARY_STORAGE_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          itineraryData: updatedManifest,
          storageConfig: {
            country: country,
            itineraryName: folderPath,
            filename: 'rag_documents/manifest.json',
            forceUpdate: true
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save manifest: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        return result;
      } else {
        throw new Error(result.error || 'Failed to save manifest to backend');
      }
    } catch (error) {
      throw new Error(`Error saving RAG manifest: ${error.message}`);
    }
  }

  /**
   * Create a new RAG manifest
   */
  createNewManifest(metadata) {
    const baseUrl = this.generateDocumentsBaseUrl(metadata);

    return {
      itineraryId: metadata.id?.toString() || Date.now().toString(),
      itineraryName: metadata.name || 'Unknown Itinerary',
      lastUpdated: new Date().toISOString(),
      baseUrl: baseUrl,
      documents: [],
      globalKeywords: []
    };
  }

  /**
   * Sanitize keyword: convert spaces to underscores, remove special chars except accents
   */
  sanitizeKeyword(keyword) {
    if (!keyword || typeof keyword !== 'string') return '';
    return keyword
      .trim()
      .replace(/\s+/g, '_')  // Replace spaces with underscores
      .replace(/[^\w\u00C0-\u017F_-]/g, '')  // Keep alphanumeric, accents, underscores, hyphens
      .toLowerCase();
  }

  /**
   * Validate RAG document structure
   */
  validateDocument(document) {
    const errors = [];

    if (!document.id) errors.push('Document ID is required');
    if (!document.title) errors.push('Document title is required');
    if (!document.category) errors.push('Document category is required');
    if (!Array.isArray(document.keywords)) errors.push('Keywords must be an array');
    if (!Array.isArray(document.relevantStops)) errors.push('Relevant stops must be an array');
    if (typeof document.priority !== 'number' || document.priority < 1 || document.priority > 3) {
      errors.push('Priority must be a number between 1 and 3');
    }

    // Check for spaces in keywords
    if (document.keywords && Array.isArray(document.keywords)) {
      const keywordsWithSpaces = document.keywords.filter(k => /\s/.test(k));
      if (keywordsWithSpaces.length > 0) {
        errors.push(`Keywords cannot contain spaces. Use underscores instead: ${keywordsWithSpaces.join(', ')}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Add document to manifest
   */
  addDocumentToManifest(manifest, document) {
    const validation = this.validateDocument(document);
    if (!validation.isValid) {
      throw new Error(`Invalid document: ${validation.errors.join(', ')}`);
    }

    // Check for duplicate IDs
    if (manifest.documents.some(doc => doc.id === document.id)) {
      throw new Error(`Document with ID '${document.id}' already exists`);
    }

    const updatedManifest = {
      ...manifest,
      documents: [...manifest.documents, document],
      lastUpdated: new Date().toISOString()
    };

    return updatedManifest;
  }

  /**
   * Update document in manifest
   */
  updateDocumentInManifest(manifest, documentId, updates) {
    const documentIndex = manifest.documents.findIndex(doc => doc.id === documentId);
    if (documentIndex === -1) {
      throw new Error(`Document with ID '${documentId}' not found`);
    }

    const updatedDocument = { ...manifest.documents[documentIndex], ...updates };
    const validation = this.validateDocument(updatedDocument);
    if (!validation.isValid) {
      throw new Error(`Invalid document updates: ${validation.errors.join(', ')}`);
    }

    const updatedDocuments = [...manifest.documents];
    updatedDocuments[documentIndex] = updatedDocument;

    return {
      ...manifest,
      documents: updatedDocuments,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Remove document from manifest
   */
  removeDocumentFromManifest(manifest, documentId) {
    const documentExists = manifest.documents.some(doc => doc.id === documentId);
    if (!documentExists) {
      throw new Error(`Document with ID '${documentId}' not found`);
    }

    return {
      ...manifest,
      documents: manifest.documents.filter(doc => doc.id !== documentId),
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Generate global keywords from documents with multi-language support
   */
  generateGlobalKeywords(documents) {
    const keywordCounts = {};
    const keywordToDocuments = {};

    // Count keyword frequencies and track which documents contain them
    documents.forEach(doc => {
      doc.keywords.forEach(keyword => {
        keywordCounts[keyword] = (keywordCounts[keyword] || 0) + 1;
        if (!keywordToDocuments[keyword]) {
          keywordToDocuments[keyword] = [];
        }
        keywordToDocuments[keyword].push(doc.id);
      });
    });

    // Create global keywords for all unique keywords
    const globalKeywords = [];
    Object.entries(keywordCounts).forEach(([keyword, count]) => {
      const formattedLabel = this.formatKeywordLabel(keyword);
      const formattedDescription = `Related to ${keyword.replace(/_/g, ' ')}`;

      // Include ALL keywords (not just ones appearing in multiple documents)
      globalKeywords.push({
        key: keyword,
        labels: {
          en: formattedLabel
          // Other languages can be added manually or via translation service
        },
        descriptions: {
          en: formattedDescription
          // Other languages can be added manually or via translation service
        },
        relatedDocuments: keywordToDocuments[keyword],
        relevantStops: [...new Set(
          documents
            .filter(doc => doc.keywords.includes(keyword))
            .flatMap(doc => doc.relevantStops)
            // Remove language suffix from stop IDs
            .map(stopId => stopId.replace(/_[a-z]{2}(-[a-z]{2})?$/i, ''))
        )]
      });
    });

    return globalKeywords;
  }

  /**
   * Get translated label for a keyword
   * @param {Object} keywordObj - The keyword object with labels/descriptions
   * @param {string} language - Language code (e.g., 'en', 'pt', 'es')
   * @param {string} field - Field to get ('labels' or 'descriptions')
   * @returns {string} - Translated text or English fallback
   */
  getTranslatedText(keywordObj, language = 'en', field = 'labels') {
    if (!keywordObj || !keywordObj[field]) {
      return '';
    }

    // Try to get the requested language, fallback to English, then to first available
    return keywordObj[field][language] ||
           keywordObj[field]['en'] ||
           Object.values(keywordObj[field])[0] ||
           '';
  }

  /**
   * Add or update translation for a keyword
   * @param {Object} keywordObj - The keyword object
   * @param {string} language - Language code
   * @param {string} label - Translated label
   * @param {string} description - Translated description
   * @returns {Object} - Updated keyword object
   */
  addKeywordTranslation(keywordObj, language, label, description) {
    const updated = { ...keywordObj };

    if (!updated.labels) updated.labels = {};
    if (!updated.descriptions) updated.descriptions = {};

    if (label) updated.labels[language] = label;
    if (description) updated.descriptions[language] = description;

    return updated;
  }

  /**
   * Update manifest with regenerated global keywords
   * Preserves existing translations when regenerating
   */
  updateManifestGlobalKeywords(manifest) {
    if (!manifest || !manifest.documents || manifest.documents.length === 0) {
      return {
        ...manifest,
        globalKeywords: []
      };
    }

    // Create a map of existing translations
    const existingTranslations = {};
    if (manifest.globalKeywords) {
      manifest.globalKeywords.forEach(kw => {
        existingTranslations[kw.key] = {
          labels: kw.labels || {},
          descriptions: kw.descriptions || {}
        };
      });
    }

    // Generate new keywords
    const globalKeywords = this.generateGlobalKeywords(manifest.documents);

    // Merge existing translations back in
    const mergedKeywords = globalKeywords.map(kw => {
      if (existingTranslations[kw.key]) {
        // Preserve all existing translations, add new English ones
        return {
          ...kw,
          labels: { ...kw.labels, ...existingTranslations[kw.key].labels },
          descriptions: { ...kw.descriptions, ...existingTranslations[kw.key].descriptions }
        };
      }
      return kw;
    });

    return {
      ...manifest,
      globalKeywords: mergedKeywords,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Format keyword for display
   * Handles Unicode characters properly (accents, special chars)
   */
  formatKeywordLabel(keyword) {
    return keyword
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Export manifest as JSON file
   */
  exportManifest(manifest, filename = 'manifest.json') {
    const manifestData = JSON.stringify(manifest, null, 2);
    const blob = new Blob([manifestData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Import manifest from JSON file
   */
  async importManifest(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const manifest = JSON.parse(event.target.result);

          // Basic validation
          if (!manifest.itineraryId || !manifest.itineraryName || !Array.isArray(manifest.documents)) {
            throw new Error('Invalid manifest structure');
          }

          resolve(manifest);
        } catch (error) {
          reject(new Error(`Failed to parse manifest: ${error.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Get document statistics
   */
  getManifestStats(manifest) {
    if (!manifest || !manifest.documents) {
      return {
        totalDocuments: 0,
        totalKeywords: 0,
        categoryCounts: {},
        priorityCounts: {},
        totalWordCount: 0
      };
    }

    const stats = {
      totalDocuments: manifest.documents.length,
      totalKeywords: new Set(manifest.documents.flatMap(doc => doc.keywords)).size,
      categoryCounts: {},
      priorityCounts: { 1: 0, 2: 0, 3: 0 },
      totalWordCount: manifest.documents.reduce((sum, doc) => sum + (doc.wordCount || 0), 0)
    };

    manifest.documents.forEach(doc => {
      // Count categories
      if (doc.category) {
        stats.categoryCounts[doc.category] = (stats.categoryCounts[doc.category] || 0) + 1;
      }

      // Count priorities
      stats.priorityCounts[doc.priority] = (stats.priorityCounts[doc.priority] || 0) + 1;
    });

    return stats;
  }
}

export const ragService = new RAGService();
